import * as React from 'react'

import type { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'

import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import { ConditionTypes } from 'modules/automations/lib/enums'
import { Operation } from 'modules/automations/store/models/Operation'
import { ITriggerCondition, Trigger } from 'modules/automations/store/models/Trigger'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import ValueSelect from './ValueSelect'

interface Props {
  disabled: boolean
  form: Form
  operation: BranchOperation
  label: string
  loadingCustomFields: boolean
  customFieldsCollection: CustomFieldsCollection
  hideHasChanged: boolean
  condition?: ITriggerCondition
  extensionFeatureHasConditions: boolean
  contractVersions: ContractVersions
}

export const Selects = observer((props: Props) => {
  const {
    disabled,
    form,
    operation,
    label,
    loadingCustomFields,
    customFieldsCollection,
    contractVersions,
    condition,
    hideHasChanged,
    extensionFeatureHasConditions,
  } = props

  // Determines if the "has_changed" operator should be available
  const shouldRenderHasChanged = !hideHasChanged

  const operatorOptions = React.useMemo(() => {
    const elements = [
      {
        label: i18n.t('automations.operations.equals'),
        value: Operation.Operators.equals,
      },
      {
        label: i18n.t('automations.operations.not_equals'),
        value: Operation.Operators.not_equals,
      },
    ]

    if (shouldRenderHasChanged) {
      elements.push({
        label: i18n.t('automations.operations.has_changed'),
        value: Operation.Operators.has_changed,
      })
    }

    return elements
  }, [shouldRenderHasChanged])

  const newIsTextField =
    condition?.type === ConditionTypes.String &&
    form.get('operator').value !== Operation.Operators.has_changed

  const oldIsTextField = React.useMemo(() => {
    if (
      operation.attribute === Trigger.Attributes.custom_fields ||
      operation.attribute === Trigger.Attributes.custom_resources
    ) {
      return false
    }

    if (operation.id !== null) return true

    switch (operation.attribute) {
      case Trigger.Attributes.first_name:
      case Trigger.Attributes.last_name:
      case Trigger.Attributes.age:
      case Trigger.Attributes.city:
      case Trigger.Attributes.state:
      case Trigger.Attributes.identifier:
      case Trigger.Attributes.bank_number:
      case Trigger.Attributes.addressLine1:
      case Trigger.Attributes.addressLine2:
      case Trigger.Attributes.postalCode:
      case Trigger.Attributes.social_security_number:
        return form.get('operator').value !== Operation.Operators.has_changed
      default:
        return false
    }
  }, [operation, form])

  const isTextField = extensionFeatureHasConditions ? newIsTextField : oldIsTextField

  const isNumberField =
    condition?.type === ConditionTypes.Number &&
    form.get('operator').value !== Operation.Operators.has_changed

  const valueIsVisible =
    !isTextField && form.get('operator').value !== Operation.Operators.has_changed

  // Note: original condition always returns true since an attribute can't be both document_public and gender at the same time
  const shouldRenderOperator =
    operation.attribute !== Trigger.Attributes.document_public ||
    operation.attribute !== Trigger.Attributes.gender

  return (
    <>
      <FormRow>
        {shouldRenderOperator && (
          <Select
            none={i18n.t('common.select_one')}
            readonly={disabled}
            field={form.get('operator')}
            label={label}
            options={operatorOptions}
          />
        )}
      </FormRow>
      <FormRow>
        {valueIsVisible && (
          <ValueSelect
            disabled={disabled}
            form={form}
            operation={operation}
            loadingCustomFields={loadingCustomFields}
            customFieldsCollection={customFieldsCollection}
            condition={condition}
            extensionFeatureHasConditions={extensionFeatureHasConditions}
            contractVersions={contractVersions}
          />
        )}
        {isTextField && (
          <Text
            label={i18n.t('automations.actions.branch.value')}
            field={form.get('value')}
            readonly={disabled}
          />
        )}
        {isNumberField && (
          <Text
            label={i18n.t('automations.actions.branch.value')}
            field={form.get('value')}
            type="number"
            readonly={disabled}
          />
        )}
      </FormRow>
    </>
  )
})
